import { Choice } from 'APP/Components/Intake/IntakeSingleChoice/types'
import { MappedEnrichedEncounter } from 'APP/Services/Coredata/index.types'
import { createContext, useContext } from 'react'

export interface SelectedAddressInfo {
  address: string
  phoneNumber?: string
  placeType: string
  name: string
  placeId: string
}

export interface SelectedAddress {
  addressInfo: SelectedAddressInfo
  payloadTemplate: string
  payload: string
}

export interface ChatPostProps {
  type?: string
  failed?: boolean
  errorMessage?: string
  resource?: string
  utterance?: string
  note?: string
  button?: string
  place_type?: string
  payload_template?: string
  question?: {
    data?: { choices?: unknown[] }
    note?: string
    reply_props?: Record<string, unknown>
    type?: string
  }
  choices?: Choice[]
  config?: {
    allow_none?: boolean
    max_selection_count?: number
    min_selection_count?: number
  }
  selectedValues?: string[]
  question_id?: string
  resource_id?: string
  microapp?: { id?: string }
  searchterm?: string
  [key: string]: unknown
  title?: string
  payload?: {
    slot_to_set?: string
  }
}

// TODO: the correct type for these objects is a union of (EnrichedPost | PendingPost | TypingIndicatorFakePost)
export interface ChatPost {
  id?: string
  message?: string
  create_at?: number
  isLocal?: boolean
  channel_id?: string
  user_id?: string
  file_ids?: string[]
  props?: ChatPostProps
  actionContext?: {
    href?: string
    amount?: number
    description?: string
    encounterId?: string
  }
}

export interface ChatSingleChoiceOption {
  text?: string
  display?: string
  value?: unknown
}

export interface ChatMultiChoiceOption {
  display: string
  exclusive?: boolean
  group_id?: string
  value?: unknown
}

export interface SendTextWithPayloadPayload {
  message: string
  payload?: string
  payloadTemplate?: string
  flags?: Record<string, unknown>
}

export interface ChatSelectedFile {
  fileName: string
  [key: string]: unknown
}

// TODO: model this upstream in PatientHistory sagas/reducers
// This is a meld of practitioner objects and a specially shaped object that represents the current user
export interface ChatUser {
  id: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  app_id?: string
  [key: string]: unknown
}

export interface ChatContextValue {
  channelId?: string
  userId?: string
  setSelectedAddress: (value: SelectedAddress | null) => void
  sendSingleChoiceAnswer?: (post: ChatPost | undefined, choice: ChatSingleChoiceOption) => void
  sendMultiChoiceAnswer?: (post: ChatPost | undefined, choices: ChatMultiChoiceOption[]) => void
  users: ChatUser[]
  episode?: {
    stateId?: string
    outcomeId?: string
    assigneeId?: string | number
    assigneePicture?: string
    assigneeFirstName?: string
    [key: string]: unknown
  }
  episodeMembers?: { userId?: string; lastViewedAt?: number }[]
  onMessageSend?: (message: string) => void
  // TODO: convert type to Channel once ChatScreen is migrated to TS
  conversation?: {
    fetchingPosts?: boolean
    allOlderPostsFetched?: boolean
    missingData?: boolean
    [key: string]: unknown
  }
  selectedAddress?: SelectedAddress | null
  sendTextWithPayload?: (payload: SendTextWithPayloadPayload) => void
  sendMessage?: (args: { message?: string }) => void
  onFilePicked?: (fileData: Record<string, unknown>) => void
  isConnected?: boolean
  isInactiveAndEpisodeInactive?: boolean
  hasEligibleBrands?: boolean
  lastPost?: ChatPost
  selectedFile?: ChatSelectedFile | null
  setSelectedFile?: (value: ChatSelectedFile | null) => void
  order: string[]
  orderedPosts: ChatPost[]
  typingEvent?: {
    broadcast?: { channel_id?: string }
    data?: { user_id?: string }
  }
}

export interface PostContextValue {
  post?: ChatPost
  isMember?: boolean
  isLastPost?: boolean
  encounter?: MappedEnrichedEncounter
  previousPostIsSameUser?: boolean
  nextPostIsSameUser?: boolean
  previousPostIsSameDay?: boolean
  nextPostIsSameDay?: boolean
  nextPostSameTimestamp?: boolean
  postUser?: {
    avatar_url?: string
    app_id?: string
    props?: { picture?: string }
  }
}

export const ChatContext = createContext<ChatContextValue | undefined>(undefined)
export const PostContext = createContext<PostContextValue | undefined>(undefined)

export const useChatContext = (): ChatContextValue => {
  const ctx = useContext(ChatContext)
  if (!ctx) throw new Error('useChatContext must be used within a ChatContext.Provider')
  return ctx
}

export const usePostContext = (): PostContextValue => {
  const ctx = useContext(PostContext)
  if (!ctx) throw new Error('usePostContext must be used within a PostContext.Provider')
  return ctx
}
